import React, { useEffect, useMemo, useState } from 'react';
import { Clock, Save, Loader2, AlertCircle, CheckCircle2, Users, Plus, Trash2, FileText, Send, History } from 'lucide-react';
import styles from './EodConfiguration.module.css';
import { useAuth } from '../../../context/AuthContext';
import { useStore } from '../../../context/StoreContext';
import { SMIIFY_API } from '../../../config/api';

const API_BASE = `${SMIIFY_API}/integrations/whatsapp`;
const LANG_NAMES = { en: 'English', en_US: 'English (US)', en_GB: 'English (UK)', hi: 'Hindi', mr: 'Marathi', gu: 'Gujarati', ta: 'Tamil', te: 'Telugu', bn: 'Bengali', kn: 'Kannada', ml: 'Malayalam', pa: 'Punjabi' };
const langName = (code) => LANG_NAMES[code] || code || '';
const todayYmd = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());

const emptyEod = { isEnabled: false, sendTime: '21:30', dailyLimit: 1, recipients: [], templateName: '', templateLang: '', lastRunDate: '', lastRunResult: '' };

/** Body text of a template with its variables marked */
const TemplatePreview = ({ tpl }) => {
    const body = (tpl?.components || []).find((c) => c.type === 'BODY')?.text || '';
    const header = (tpl?.components || []).find((c) => c.type === 'HEADER');
    const button = ((tpl?.components || []).find((c) => c.type === 'BUTTONS')?.buttons || [])[0];
    const parts = body.split(/(\{\{[^}]+\}\})/g);
    return (
        <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 12, background: '#efeae2', fontSize: 12.5, lineHeight: 1.5 }}>
            <div style={{ padding: '10px 12px', borderRadius: '4px 12px 12px 12px', background: '#fff', boxShadow: '0 1px 1px rgba(0,0,0,.06)', whiteSpace: 'pre-wrap' }}>
                {header?.format === 'TEXT' && header.text && <div style={{ fontWeight: 800, marginBottom: 4 }}>{header.text}</div>}
                {parts.map((p, i) => (/^\{\{/.test(p)
                    ? <span key={i} style={{ padding: '0 3px', borderRadius: 4, background: '#ebe4ff', color: '#471ea7', fontWeight: 800 }}>{p.replace(/[{}]/g, '')}</span>
                    : <span key={i}>{p}</span>))}
                {button && <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee', textAlign: 'center', color: '#0f8a74', fontWeight: 800 }}>{button.text}</div>}
            </div>
        </div>
    );
};

export default function EodConfiguration() {
    const { user } = useAuth();
    const userId = user?._id || user?.id || (user ? 'me' : '');   // a plain id: loads again only when the login changes
    const { activeStore } = useStore();
    const isHQActive = !activeStore || activeStore.isHQ === true || activeStore.id === 'global_hq' || activeStore._id === 'global_hq';
    const targetStoreId = isHQActive ? null : (activeStore?._id || activeStore?.id);
    const storeHeader = targetStoreId || 'global_hq';

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [newPhone, setNewPhone] = useState('');
    const [newName, setNewName] = useState('');
    const [eod, setEod] = useState(emptyEod);
    const [inherited, setInherited] = useState(null);      // HQ settings a branch follows while it has none of its own
    const [templates, setTemplates] = useState(null);      // null = loading, [] = none / no line
    const [templateError, setTemplateError] = useState('');

    const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeHeader }), [storeHeader]);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4500);
    };

    // settings
    useEffect(() => {
        if (!userId) return undefined;
        let alive = true;
        (async () => {
            setIsLoading(true);
            try {
                const url = targetStoreId ? `${API_BASE}?storeId=${targetStoreId}` : API_BASE;
                const res = await fetch(url, { headers });
                const j = await res.json().catch(() => ({}));
                if (!alive) return;
                const own = { ...emptyEod, ...(j?.data?.eodAutomation || {}) };
                setEod(own);
                const eff = j?.data?.effectiveEod;
                setInherited(targetStoreId && eff?.source === 'HQ' && (eff.isEnabled || eff.recipients?.length) ? eff : null);
            } catch (e) {
                if (alive) showToast('Could not load the day closing settings.', 'error');
            } finally { if (alive) setIsLoading(false); }
        })();
        return () => { alive = false; };
    }, [targetStoreId, userId, headers]);

    // approved Utility templates on this branch's (or HQ's) WhatsApp line
    useEffect(() => {
        if (!userId) return undefined;
        let alive = true;
        setTemplates(null); setTemplateError('');
        fetch(`${SMIIFY_API}/templates`, { headers })
            .then((res) => res.json().then((j) => ({ ok: res.ok, j })))
            .then(({ ok, j }) => {
                if (!alive) return;
                if (!ok || !j.success) { setTemplates([]); setTemplateError(j.message || 'Connect a WhatsApp line first.'); return; }
                setTemplates((j.data || []).filter((t) => t.status === 'APPROVED' && String(t.category).toUpperCase() === 'UTILITY'));
            })
            .catch(() => { if (alive) { setTemplates([]); setTemplateError('Could not load your templates.'); } });
        return () => { alive = false; };
    }, [userId, headers]);

    const versions = useMemo(() => (templates || []).filter((t) => t.name === eod.templateName), [templates, eod.templateName]);
    const names = useMemo(() => [...new Set((templates || []).map((t) => t.name))], [templates]);
    const chosen = useMemo(() => versions.find((t) => t.lang === eod.templateLang) || versions[0] || null, [versions, eod.templateLang]);
    const chosenMissing = Boolean(eod.templateName) && templates && templates.length > 0 && !versions.length;

    const set = (patch) => setEod((prev) => ({ ...prev, ...patch }));
    const pickTemplate = (name) => {
        const first = (templates || []).find((t) => t.name === name);
        set({ templateName: name, templateLang: first?.lang || '' });
    };

    const addRecipient = () => {
        const phone = newPhone.replace(/\D/g, '').slice(-10);
        if (phone.length !== 10) { showToast('Enter a valid 10-digit number.', 'error'); return; }
        if (eod.recipients.some((r) => r.phone === phone)) { showToast('Number already added.', 'error'); return; }
        if (eod.recipients.length >= 10) { showToast('You can add up to 10 numbers.', 'error'); return; }
        set({ recipients: [...eod.recipients, { phone, name: newName.trim(), isActive: true }] });
        setNewPhone(''); setNewName('');
    };
    const toggleRecipient = (idx) => set({ recipients: eod.recipients.map((r, i) => (i === idx ? { ...r, isActive: !r.isActive } : r)) });
    const removeRecipient = (idx) => set({ recipients: eod.recipients.filter((_, i) => i !== idx) });

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const res = await fetch(`${API_BASE}/eod`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...eod, templateLang: chosen?.lang || eod.templateLang, storeId: targetStoreId }),
            });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) throw new Error(j.message || 'Could not save.');
            setEod({ ...emptyEod, ...(j.data?.eodAutomation || eod) });
            setInherited(null);
            showToast(j.message || 'Saved.');
        } catch (err) {
            showToast(err.message, 'error');
        } finally { setIsSaving(false); }
    };

    // test: today's closing of this branch, now, to the active numbers
    const sendNow = async () => {
        const phones = eod.recipients.filter((r) => r.isActive).map((r) => r.phone);
        if (!phones.length) { showToast('Add an active number first.', 'error'); return; }
        if (!chosen) { showToast('Choose a template first.', 'error'); return; }
        setIsSending(true);
        try {
            const res = await fetch(`${SMIIFY_API}/daily-report/send-whatsapp`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ date: todayYmd(), templateName: chosen.name, language: chosen.lang, phones }),
            });
            const j = await res.json().catch(() => ({}));
            showToast(j.message || (res.ok ? 'Sent.' : 'Could not send.'), j.success ? 'success' : 'error');
        } catch (err) {
            showToast(err.message, 'error');
        } finally { setIsSending(false); }
    };

    if (isLoading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} color="var(--h-color-primary-600)" /></div>;
    }

    const activeCount = eod.recipients.filter((r) => r.isActive).length;

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIconWrap}><Clock size={16} strokeWidth={2.5} /></div>
                    <div>
                        <h2 className={styles.headerTitle}>Day closing on WhatsApp</h2>
                        <p className={styles.headerSubtitle}>
                            {isHQActive ? 'HQ settings: used by every branch that has none of its own.' : `Settings for ${activeStore?.storeName || 'this branch'} only.`}
                        </p>
                    </div>
                </div>
            </div>

            {toast.show && (
                <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}>
                    {toast.type === 'error' ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />} <span>{toast.message}</span>
                </div>
            )}

            <form onSubmit={handleSave}>
                <div className={styles.formBody}>
                    {inherited && (
                        <div className={styles.settingBox} style={{ background: 'var(--h-color-primary-50, #f5f6ff)' }}>
                            <div className={styles.settingText}>
                                <strong>This branch follows the HQ settings</strong>
                                <span>
                                    {inherited.isEnabled ? `Sent every day at ${inherited.sendTime}` : 'Automatic sending is off at HQ'}
                                    {inherited.templateName ? ` with "${inherited.templateName}"` : ''}. Save here to give this branch its own settings.
                                </span>
                            </div>
                        </div>
                    )}

                    <div className={styles.settingBox}>
                        <div className={styles.settingText}>
                            <strong>Send the day closing every day</strong>
                            <span>Goes out on your WhatsApp line at the time below, with the template you choose.</span>
                        </div>
                        <label className={styles.switch}>
                            <input type="checkbox" checked={eod.isEnabled} onChange={(e) => set({ isEnabled: e.target.checked })} aria-label="Send the day closing every day" />
                            <span className={styles.slider} />
                        </label>
                    </div>

                    <div className={styles.grid2Col}>
                        <div className={styles.formGroup}>
                            <label htmlFor="eod-time"><Clock size={12} /> Time (India)</label>
                            <input id="eod-time" type="time" className={styles.inputField} value={eod.sendTime} onChange={(e) => set({ sendTime: e.target.value })} disabled={!eod.isEnabled} style={{ opacity: eod.isEnabled ? 1 : 0.5 }} />
                        </div>
                        <div className={styles.formGroup}>
                            <label><History size={12} /> Last automatic send</label>
                            <div className={styles.inputField} style={{ display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 700, color: 'var(--h-color-neutral-600, #58585e)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={eod.lastRunResult}>
                                {eod.lastRunResult || 'Not sent yet'}
                            </div>
                        </div>
                    </div>

                    {/* ---------------- template + version ---------------- */}
                    <div className={styles.recipientCard}>
                        <h3 className={styles.recipientCardTitle}><FileText size={16} color="var(--h-color-primary-600)" /> Report template</h3>
                        {templates === null ? (
                            <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--h-color-neutral-500, #797980)' }}><Loader2 size={12} style={{ animation: 'spin 1s linear infinite', verticalAlign: '-2px' }} /> Loading your templates…</p>
                        ) : !templates.length ? (
                            <div className={styles.emptyState}><p>{templateError || 'No approved Utility template yet. Create the day closing template in WhatsApp Manager; it shows here once Meta approves it.'}</p></div>
                        ) : (
                            <>
                                <div className={styles.grid2Col}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="eod-template">Template</label>
                                        <select id="eod-template" className={styles.inputField} value={versions.length ? eod.templateName : ''} onChange={(e) => pickTemplate(e.target.value)}>
                                            <option value="" disabled>Choose a template</option>
                                            {names.map((n) => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="eod-version">Version (language)</label>
                                        <select id="eod-version" className={styles.inputField} value={chosen?.lang || ''} onChange={(e) => set({ templateLang: e.target.value })} disabled={versions.length < 2}>
                                            {!versions.length && <option value="">—</option>}
                                            {versions.map((v) => <option key={v.lang} value={v.lang}>{langName(v.lang)}</option>)}
                                        </select>
                                    </div>
                                </div>
                                {chosenMissing && (
                                    <p style={{ margin: '8px 0 0', fontSize: 12, fontWeight: 700, color: 'var(--h-color-danger-600, #be1025)' }}>
                                        "{eod.templateName}" is no longer approved on your line. Choose another template.
                                    </p>
                                )}
                                {chosen && <TemplatePreview tpl={chosen} />}
                            </>
                        )}
                    </div>

                    {/* ---------------- numbers ---------------- */}
                    <div className={styles.recipientCard}>
                        <h3 className={styles.recipientCardTitle}><Users size={16} color="var(--h-color-primary-600)" /> Send to ({activeCount} active)</h3>
                        <div className={styles.addInputGroup}>
                            <input type="text" className={styles.inputField} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name (optional)" style={{ maxWidth: 170 }} />
                            <input type="text" inputMode="numeric" className={styles.inputField} value={newPhone} onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }} placeholder="10-digit mobile number" />
                            <button type="button" className={styles.addBtn} onClick={addRecipient}><Plus size={14} /> Add</button>
                        </div>

                        {eod.recipients.length > 0 ? (
                            <div className={styles.listContainer}>
                                {eod.recipients.map((rec, idx) => (
                                    <div key={rec.phone} className={styles.listItem}>
                                        <div className={styles.itemInfo}>
                                            <span className={rec.isActive ? styles.itemPhone : styles.itemPhoneInactive}>{rec.name ? `${rec.name} · ` : ''}+91 {rec.phone}</span>
                                            <span className={`${styles.badge} ${rec.isActive ? styles.badgeActive : styles.badgeInactive}`}>{rec.isActive ? 'Active' : 'Paused'}</span>
                                        </div>
                                        <div className={styles.itemActions}>
                                            <button type="button" className={styles.toggleBtn} onClick={() => toggleRecipient(idx)}>{rec.isActive ? 'Pause' : 'Resume'}</button>
                                            <button type="button" className={styles.deleteBtn} onClick={() => removeRecipient(idx)} aria-label={`Remove ${rec.phone}`}><Trash2 size={14} /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.emptyState}><p>No numbers yet. Add the owner's number to get the report.</p></div>
                        )}
                    </div>
                </div>

                <div className={styles.footer} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    {!isHQActive ? (
                        <button type="button" className={styles.addBtn} onClick={sendNow} disabled={isSending || !chosen || !activeCount} title="Sends today's closing of this branch now, with this template">
                            {isSending ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />} Send today's report now
                        </button>
                    ) : <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--h-color-neutral-500, #797980)', alignSelf: 'center' }}>Select a branch to send a test report.</span>}
                    <button type="submit" className={styles.saveBtn} disabled={isSaving}>
                        {isSaving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />} Save settings
                    </button>
                </div>
            </form>
        </div>
    );
}
